import OfflineStore from "./OfflineStore";

/**
 * Offline Sync Queue — replays pending mutations when connectivity is restored.
 *
 * Listens for the browser "online" event and replays queued writes in FIFO order.
 * Emits custom events on window for UI feedback:
 *   - "offline-sync-start"    { detail: { count } }
 *   - "offline-sync-progress" { detail: { completed, total, current } }
 *   - "offline-sync-complete" { detail: { succeeded, failed } }
 *   - "offline-sync-error"    { detail: { entry, error } }
 */
export default class OfflineSyncQueue {
	static #axiosInstance = null;
	static #syncing = false;
	static #listening = false;
	static #onlineHandler = null;
	static #simulationHandler = null;

	/**
	 * Initialize the sync queue with the axios instance to replay through.
	 * Starts listening for online events and simulated-offline toggle.
	 * @param {import("axios").AxiosInstance} axiosInstance
	 */
	static Init(axiosInstance) {
		this.#axiosInstance = axiosInstance;

		if (this.#listening) return;
		this.#listening = true;

		this.#onlineHandler = () => this.Sync();
		window.addEventListener("online", this.#onlineHandler);

		// Also sync when simulated offline mode is turned off
		this.#simulationHandler = (e) => {
			if (!e.detail.simulateOffline && e.detail.effectiveOnline) {
				this.Sync();
			}
		};
		window.addEventListener("offline-simulation-change", this.#simulationHandler);

		// Flush any pending mutations left over from a previous session
		this.Sync();
	}

	/**
	 * Clean up the sync queue listener.
	 */
	static Destroy() {
		if (this.#onlineHandler) {
			window.removeEventListener("online", this.#onlineHandler);
			this.#onlineHandler = null;
		}
		if (this.#simulationHandler) {
			window.removeEventListener("offline-simulation-change", this.#simulationHandler);
			this.#simulationHandler = null;
		}
		this.#listening = false;
		this.#axiosInstance = null;
	}

	/**
	 * Attempt to sync all pending mutations.
	 * Can be called manually or triggered by the "online" event.
	 */
	static async Sync() {
		if (this.#syncing) return;
		if (!navigator.onLine) return;

		const pending = await OfflineStore.GetPendingMutations();
		if (!pending.length) return;

		this.#syncing = true;
		this.#emit("offline-sync-start", { count: pending.length });

		let succeeded = 0;
		let failed = 0;
		const classesModified = new Set();

		for (let i = 0; i < pending.length; i++) {
			const entry = pending[i];

			this.#emit("offline-sync-progress", {
				completed: i,
				total: pending.length,
				current: entry,
			});

			try {
				await this.#replay(entry);
				await OfflineStore.ClearMutation(entry.id);
				classesModified.add(entry.class_name);
				succeeded++;
			} catch (error) {
				if (error.response) {
					// Server error (4xx/5xx) — permanent failure
					await OfflineStore.FailMutation(entry.id);
				}
				// Network error (no response) — leave as pending for next retry
				failed++;
				this.#emit("offline-sync-error", { entry, error });
			}
		}

		// Invalidate cache for modified classes so fresh data is fetched
		for (const className of classesModified) {
			await OfflineStore.ClearClassCache(className);
		}

		this.#syncing = false;
		this.#emit("offline-sync-complete", { succeeded, failed });
	}

	/**
	 * Replay a single queued mutation through the axios instance.
	 * Strips the _model config to prevent the interceptor from re-caching/re-queuing.
	 */
	static async #replay(entry) {
		const { method, url, payload, config } = entry;
		const replayConfig = {
			headers: config?.headers || {},
			params: config?.params,
			_replay: true, // flag to prevent re-interception
		};

		if (method === "get") {
			return this.#axiosInstance.get(url, replayConfig);
		} else if (method === "delete") {
			return this.#axiosInstance.delete(url, replayConfig);
		} else {
			return this.#axiosInstance[method](url, payload, replayConfig);
		}
	}

	/**
	 * Emit a custom event on window for UI components to listen to.
	 */
	static #emit(eventName, detail) {
		window.dispatchEvent(new CustomEvent(eventName, { detail }));
	}
}
